import { useEffect, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe.js';

/**
 * StripeCardUpdate — modal-style form for updating the payment method.
 *
 * Flow:
 *   1. Parent calls startPaymentMethodUpdate() → gets client_secret
 *   2. Parent mounts this component with the client_secret
 *   3. User enters new card, clicks Update
 *   4. On success, Stripe webhooks fire (payment_method.attached),
 *      Randy's backend detaches the old method and sets the new one
 *      as default for the subscription
 *   5. Parent closes the form and refetches billing state
 *
 * Uses Stripe's PaymentElement (newer than Card Element) — handles
 * Apple Pay, Google Pay, card input automatically based on what the
 * browser/device supports.
 */
export function StripeCardUpdate({ clientSecret, onSuccess, onCancel }) {
  if (!clientSecret) return null;

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#00AB39',
        colorBackground: '#FAF6F0',
        colorText: '#0E1A15',
        borderRadius: '8px',
        fontFamily: 'Manrope, system-ui, sans-serif',
      },
    },
  };

  return (
    <Elements stripe={getStripe()} options={options}>
      <CardUpdateForm onSuccess={onSuccess} onCancel={onCancel} />
    </Elements>
  );
}

function CardUpdateForm({ onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ready, setReady] = useState(false);

  // Suppress "autofocus on mount" as the element renders async
  useEffect(() => {
    if (elements) setReady(true);
  }, [elements]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!stripe || !elements) return;

    setSubmitting(true);
    setError(null);

    const { error: submitErr } = await elements.submit();
    if (submitErr) {
      setError(submitErr.message || 'Please check your card details.');
      setSubmitting(false);
      return;
    }

    const { error: confirmErr, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/dashboard/settings/billing`,
      },
      redirect: 'if_required',
    });

    if (confirmErr) {
      setError(confirmErr.message || 'Could not save the new card.');
      setSubmitting(false);
      return;
    }

    if (setupIntent?.status === 'succeeded') {
      onSuccess?.(setupIntent);
    } else {
      // User needs to complete additional auth (3DS etc) — Stripe will
      // redirect them. This branch shouldn't fire with redirect='if_required'
      // but kept defensive.
      setError('Payment setup requires additional verification. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={{ marginTop: 'var(--sp-3)' }}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {error && (
        <div className="alert alert-error" role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end', marginTop: 'var(--sp-4)' }}>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={submitting || !stripe || !ready}
          aria-busy={submitting}
        >
          {submitting ? (
            <><span className="spinner" aria-hidden="true" /> Saving…</>
          ) : (
            'Update card'
          )}
        </button>
      </div>
    </form>
  );
}
